import type {
  RippleButtonColor,
  RippleButtonShape,
  RippleButtonSize,
  RippleButtonState,
  RippleButtonVariant,
} from "./types";

// `iconOnlyHeight` / `iconOnlyIconSize` are iconOnly-mode overrides — the
// Figma icon-button geometry doesn't fit the labeled-button progression.
export const SIZE_TOKENS: Record<
  RippleButtonSize,
  {
    height: number;
    paddingX: number;
    gap: number;
    iconSize: number;
    fontSize: number;
    lineHeight: number;
    fontWeight: number;
    letterSpacing: number;
    squareRadius: number;
    iconOnlyHeight?: number;
    iconOnlyIconSize?: number;
  }
> = {
  xs: {
    height: 32,
    paddingX: 12,
    gap: 6,
    iconSize: 16,
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 500,
    letterSpacing: 0.1,
    squareRadius: 12,
    iconOnlyHeight: 40,
    iconOnlyIconSize: 24,
  },
  sm: {
    height: 40,
    paddingX: 16,
    gap: 8,
    iconSize: 20,
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 500,
    letterSpacing: 0.1,
    squareRadius: 12,
    iconOnlyHeight: 48,
    iconOnlyIconSize: 24,
  },
  md: {
    height: 56,
    paddingX: 24,
    gap: 8,
    iconSize: 20,
    fontSize: 16,
    lineHeight: 24,
    fontWeight: 500,
    letterSpacing: 0.15,
    squareRadius: 16,
  },
  lg: {
    height: 98,
    paddingX: 32,
    gap: 12,
    iconSize: 32,
    fontSize: 20,
    lineHeight: 28,
    fontWeight: 500,
    letterSpacing: 0.15,
    squareRadius: 20,
  },
  xl: {
    height: 120,
    paddingX: 40,
    gap: 16,
    iconSize: 36,
    fontSize: 28,
    lineHeight: 36,
    fontWeight: 500,
    letterSpacing: 0,
    squareRadius: 24,
    iconOnlyHeight: 64,
    iconOnlyIconSize: 40,
  },
};

// Per-state shadow tokens (Figma node 417:34272). Each key carries the
// platform-specific values so web and RN render the same visual shadow.
export type ShadowKey = "none" | "resting" | "focused" | "pressed";

export const SHADOW_TOKENS: Record<
  ShadowKey,
  {
    web: string;
    ios: {
      shadowColor: string;
      shadowOffset: { width: number; height: number };
      shadowOpacity: number;
      shadowRadius: number;
    };
    androidElevation: number;
  }
> = {
  none: {
    web: "none",
    ios: {
      shadowColor: "transparent",
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 0,
      shadowRadius: 0,
    },
    androidElevation: 0,
  },
  resting: {
    web: "0 1px 4px 0 rgba(0, 0, 0, 0.16)",
    ios: {
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.16,
      shadowRadius: 4,
    },
    androidElevation: 2,
  },
  focused: {
    web: "0 1px 4px 0 rgba(0, 0, 0, 0.08)",
    ios: {
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.08,
      shadowRadius: 4,
    },
    androidElevation: 1,
  },
  pressed: {
    web: "4px 4px 10px 0 rgba(0, 0, 0, 0.25)",
    ios: {
      shadowColor: "#000",
      shadowOffset: { width: 4, height: 4 },
      shadowOpacity: 0.25,
      shadowRadius: 10,
    },
    androidElevation: 4,
  },
};

export function resolveColors(opts: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  theme: any;
  variant: RippleButtonVariant;
  color: RippleButtonColor;
  state: RippleButtonState;
  iconOnly?: boolean;
}): {
  background: string;
  label: string;
  border: string;
  shadow: ShadowKey;
  ripple: string;
} {
  const { theme, variant, color, state, iconOnly = false } = opts;
  const palette = theme.colors[color] ?? theme.colors.primary;
  const neutral = theme.colors.neutral;
  const onPrimary = neutral[100] ?? "#FFFFFF";

  if (state === "disabled") {
    return {
      background: neutral[90],
      label: neutral[50],
      border: variant === "outline" ? neutral[80] : "transparent",
      shadow: "none",
      ripple: neutral[50],
    };
  }

  switch (variant) {
    case "primary": {
      // Figma node 417:34272.
      const map: Record<Exclude<RippleButtonState, "disabled">, string> = {
        normal: palette[30],
        hovered: palette[20],
        focused: palette[40],
        pressed: palette[20],
      };
      const label = state === "hovered" ? palette[90] : onPrimary;
      const shadow: ShadowKey =
        state === "pressed"
          ? "pressed"
          : state === "focused"
            ? "focused"
            : "resting";
      return {
        background: map[state],
        label,
        border: "transparent",
        shadow,
        ripple: onPrimary,
      };
    }

    case "outline": {
      const map: Record<Exclude<RippleButtonState, "disabled">, string> = {
        normal: "transparent",
        hovered: palette[95],
        focused: palette[90],
        pressed: palette[95],
      };
      return {
        background: map[state],
        label: palette[40],
        border: palette[40],
        shadow: "none",
        ripple: palette[40],
      };
    }

    case "toggle": {
      // Figma node 417:34653. Focused fill kept identical to hovered (Figma's
      // overlay-darken isn't replicated — close enough visually).
      const map: Record<Exclude<RippleButtonState, "disabled">, string> = {
        normal: palette[95],
        hovered: palette[90],
        focused: palette[90],
        pressed: palette[90],
      };
      const label = state === "normal" ? palette[10] : palette[20];
      const shadow: ShadowKey = state === "pressed" ? "pressed" : "resting";
      return {
        background: map[state],
        label,
        border: "transparent",
        shadow,
        ripple: palette[20],
      };
    }

    case "text":
    default: {
      // M3: a standard icon-only button uses a neutral state layer; labeled
      // text buttons keep the brand tint. Ripple follows the hover tone.
      const tone = iconOnly ? neutral : palette;
      const map: Record<Exclude<RippleButtonState, "disabled">, string> = {
        normal: "transparent",
        hovered: tone[95],
        focused: tone[90],
        pressed: tone[95],
      };
      return {
        background: map[state],
        label: palette[40],
        border: "transparent",
        shadow: "none",
        ripple: iconOnly ? neutral[30] : palette[40],
      };
    }
  }
}

// Saturating radius — clamps to half the shorter axis at render time, so a
// custom height still yields a perfect pill/circle without recomputing.
export const ROUND_RADIUS = 9999;

export function resolveRadius(
  shape: RippleButtonShape,
  size: RippleButtonSize,
): number {
  if (shape === "round") return ROUND_RADIUS;
  return SIZE_TOKENS[size].squareRadius;
}
export function darken(input: string, amount: number): string {
  if (!input || amount <= 0) return input;
  const factor = Math.max(0, 1 - amount);
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  const toHex = (n: number) => clamp(n).toString(16).padStart(2, "0");

  if (input.startsWith("#")) {
    let hex = input.slice(1);
    if (hex.length === 3)
      hex = hex
        .split("")
        .map((c) => c + c)
        .join("");
    if (hex.length === 8) hex = hex.slice(0, 6);
    if (hex.length !== 6) return input;
    const r = parseInt(hex.slice(0, 2), 16) * factor;
    const g = parseInt(hex.slice(2, 4), 16) * factor;
    const b = parseInt(hex.slice(4, 6), 16) * factor;
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
  if (input.startsWith("rgb(") || input.startsWith("rgba(")) {
    const inner = input.slice(input.indexOf("(") + 1, -1);
    const parts = inner.split(",").map((p) => p.trim());
    if (parts.length < 3) return input;
    const r = parseFloat(parts[0]) * factor;
    const g = parseFloat(parts[1]) * factor;
    const b = parseFloat(parts[2]) * factor;
    const a = parts[3];
    return a
      ? `rgba(${clamp(r)}, ${clamp(g)}, ${clamp(b)}, ${a})`
      : `rgb(${clamp(r)}, ${clamp(g)}, ${clamp(b)})`;
  }
  return input;
}

// Convert a hex/rgb color to an rgba string at the given alpha. Returns the
// input unchanged if it can't parse. Used for android_ripple tints.
export function withAlpha(input: string, alpha: number): string {
  if (!input) return input;
  if (input === "transparent") return input;
  if (input.startsWith("rgba")) return input;
  if (input.startsWith("rgb(")) {
    const inner = input.slice(4, -1);
    return `rgba(${inner}, ${alpha})`;
  }
  if (input.startsWith("#")) {
    let hex = input.slice(1);
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((c) => c + c)
        .join("");
    }
    if (hex.length === 8) hex = hex.slice(0, 6);
    if (hex.length !== 6) return input;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return input;
}
